import { createFileRoute, redirect } from '@tanstack/react-router'
import { subDays } from 'date-fns'
import { parse } from 'valibot'

import { USER_TYPE } from '@/libs/api/types'
import { safeFormatISO } from '@/libs/utils'
import {
  selectedDcIdAtom,
  tenantStore
} from '@/routes/_protected/$tenantKey/-states'

import {
  hasPartnerHiddenConditions,
  toursSearchSchema,
  withDefaultOperatingDateRange,
  withoutPartnerHiddenConditions,
  type ToursSearchWithOperatingDate
} from './-models'

// 呼び出すたびに日付を初期化
const getDefaultSearch = (): ToursSearchWithOperatingDate => ({
  operatingDateFrom: safeFormatISO(subDays(new Date(), 7)),
  operatingDateTo: safeFormatISO(subDays(new Date(), 1))
})

export const Route = createFileRoute(
  '/_protected/$tenantKey/_performance_managements/tours/'
)({
  validateSearch: (search) => parse(toursSearchSchema, search),
  loaderDeps: ({ search }) => search,
  beforeLoad: ({ search, location, context: { auth } }) => {
    const selectedDcId = tenantStore.get(selectedDcIdAtom)
    if (search.dcId != selectedDcId) {
      throw redirect({
        to: location.pathname,
        search: {
          ...search,
          dcId: selectedDcId
        }
      })
    }

    // #5945: パートナーユーザーの URL に、画面に出していない条件（荷主・請求月・荷主名の並べ替え）が
    // 残っていたら外す。請求月は API が 400 で弾くので、残したままだと一覧が出ない
    if (
      auth.user.type === USER_TYPE.PARTNER &&
      hasPartnerHiddenConditions(search)
    ) {
      throw redirect({
        to: location.pathname,
        search: withoutPartnerHiddenConditions(search)
      })
    }

    // #4557: URL が運行日を持っていても期間上限を超えていれば既定レンジで検索する。
    // 画面には既定レンジの結果（一覧・運賃サマリー）が出るため、URL だけ元の期間の
    // まま残ると「2年分のつもりで7日分の金額を見る」ことになる。URL を実際の検索条件に
    // 揃えて食い違いを消す。運行日を持たない URL は従来どおり loader 側で補うに留める
    const normalized = withDefaultOperatingDateRange(search, getDefaultSearch())
    const hasUrlRange = Boolean(
      search.operatingDateFrom && search.operatingDateTo
    )
    const isClamped =
      normalized.operatingDateFrom !== search.operatingDateFrom ||
      normalized.operatingDateTo !== search.operatingDateTo
    if (hasUrlRange && isClamped) {
      throw redirect({ to: location.pathname, search: normalized })
    }
  },
  loader: async ({ deps, context: { auth } }) => {
    const defaultSearch = getDefaultSearch()

    // 初回レンダリング時の初期値
    // #4557: 運行日は必須のため、URL に運行日が無い / 空文字の場合は既定レンジに戻す。
    //
    // 締日基準モードで既定レンジを補わない扱い（VOC_PJ-990 #4809）は
    // withDefaultOperatingDateRange が引き継ぐ。共有の applyDefaultDateRange を
    // ここで使わないのは、本画面では「既定レンジを補うか」に加えて期間上限の
    // クランプ（beforeLoad の redirect と対にする）まで一度に決める必要があり、
    // 判定を 2 つに割ると URL とフォームの値が食い違うため。
    // 配送依頼一覧は上限クランプを持たないので共有ヘルパーのままでよい
    const initialSearch = withDefaultOperatingDateRange(deps, defaultSearch)

    return {
      authUser: auth.user,
      initialSearch,
      defaultSearch
    }
  }
})
